/**
 * VerifyEmail — landing page hit by the link in the user's inbox.
 *
 * Reads ?token=... from the URL, POSTs to /api/identity/email/verify,
 * shows success/failure, then bounces the user back to wherever makes
 * sense for their role.
 *
 * Public (works whether signed in or not).
 */
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, AlertCircle, Loader2, ShieldCheck } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [state, setState] = useState("verifying"); // verifying | success | error
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");

  useEffect(() => {
    if (!token) {
      setState("error");
      setMessage("Missing token — please use the link from your email.");
      return;
    }
    (async () => {
      try {
        const { data } = await axios.post(`${API}/api/identity/email/verify`, { token });
        setEmail(data.email || "");
        setState("success");
      } catch (e) {
        setMessage(e.response?.data?.detail || "Verification failed.");
        setState("error");
      }
    })();
  }, [token]);

  return (
    <div className="min-h-dvh no-overflow-x flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-4">
      <div data-testid="verify-email-page" className="w-full max-w-md rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xl p-6">
        {state === "verifying" && (
          <div data-testid="verify-state-loading" className="text-center">
            <Loader2 className="w-10 h-10 mx-auto text-slate-400 animate-spin" />
            <p className="mt-4 text-sm font-bold text-slate-700 dark:text-slate-200">Verifying your email…</p>
          </div>
        )}

        {state === "success" && (
          <div data-testid="verify-state-success" className="text-center">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
            <h1 className="mt-4 text-xl font-extrabold text-slate-900 dark:text-white">Email verified</h1>
            <p className="mt-1 text-[13px] text-slate-500 dark:text-slate-400">
              {email ? <>Welcome, <span className="font-semibold">{email}</span>.</> : "Your address is confirmed."}
            </p>
            <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 text-[11px] font-bold">
              <ShieldCheck className="w-3 h-3" /> Email Verified chip is now live on your profile
            </div>
            <button
              data-testid="verify-continue-btn"
              onClick={() => nav("/dashboard")}
              className="mt-6 w-full h-11 rounded-xl bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 font-bold"
            >
              Continue
            </button>
          </div>
        )}

        {state === "error" && (
          <div data-testid="verify-state-error" className="text-center">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center">
              <AlertCircle className="w-7 h-7 text-rose-600" />
            </div>
            <h1 className="mt-4 text-xl font-extrabold text-slate-900 dark:text-white">We couldn't verify that link</h1>
            <p className="mt-1 text-[13px] text-slate-500 dark:text-slate-400">{message}</p>
            <Link
              to="/settings/identity"
              data-testid="verify-resend-link"
              className="mt-6 block w-full h-11 leading-[44px] rounded-xl bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 font-bold"
            >
              Send a new verification email
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
